import { CommonModule } from '@angular/common'
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuTrigger } from '@angular/material/menu'
import { NavigationEnd, Router, RouterLink } from '@angular/router'

import { filter, retry, tap } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { AuthService } from '../../services/auth.service'
import { UserInfo } from '../../services/user/user.service'
import { UserStore } from '../../stores/user.store'
import {
  UserService,
  PrivacySettingType,
} from '../../services/user/user.service'
import { isAssessmentSettingsPageVisible } from '../../config/assessment-settings.config'
import { isFeatureAllowed } from '../../config/feature-permission.config'
import { KpiDashboardService } from '../../services/kpi-dashboard/kpi-dashboard.service'

@UntilDestroy()
@Component({
  selector: 'app-header',
  standalone: true,
  imports: [MatIcon, MatMenu, MatMenuTrigger, RouterLink, CommonModule],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  user: UserInfo | null = null
  privacySetting: PrivacySettingType | undefined = undefined
  hasKpiDashboards = false

  currentPage:
    | 'HOME'
    | 'USER_LIST'
    | 'ROLEPLAY'
    | 'ASSESSMENTS'
    | 'KPI_DASHBOARD'
    | 'DASHBOARD'
    | 'SETTINGS'
    | 'ADMIN' = 'HOME'

  constructor(
    private authService: AuthService,
    private router: Router,
    private userStore: UserStore,
    private userService: UserService,
    private kpiDashboardService: KpiDashboardService,
  ) {}

  ngOnInit(): void {
    this.userStore
      .getOneSelf({ useCache: true })
      .pipe(
        tap((user) => {
          this.user = user
        }),
        untilDestroyed(this),
      )
      .subscribe()

    this.kpiDashboardService
      .dashboards()
      .pipe(
        // dashboards$ の share({ resetOnError: true }) が前提。エラー時にキャッシュが
        // リセットされるため、この retry の再購読で新しいフェッチが張り直される
        retry({ count: 1, delay: 1000 }),
        tap((dashboards) => {
          this.hasKpiDashboards = dashboards.length > 0
        }),
        untilDestroyed(this),
      )
      .subscribe({
        // ナビ表示制御のみが目的のため、取得失敗はリンク非表示のまま握り込む
        // （ヘッダーは全ページ常設であり、未処理エラーを毎描画で飛ばさない）
        error: () => {
          this.hasKpiDashboards = false
        },
      })

    this.userService
      .getAdminOrg()
      .pipe(
        tap((adminOrg) => {
          this.privacySetting = adminOrg.privacySetting
        }),
        untilDestroyed(this),
      )
      .subscribe()

    this.currentPage = this.getCurrentPage(this.router.url)
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        this.currentPage = this.getCurrentPage(event.url)
      })
  }

  logout = () => {
    console.log('Logged out')
    return this.authService.logout().finally(() => {
      // use browser location to clear all inner states (stores, services, etc)
      window.location.href = '/login'
    })
  }

  hasMember() {
    return this.isEnablerOrAdmin() || this.user?.isManager
  }

  isAdmin() {
    return this.user?.roles.some((role) => role.code === 'SUPER_ADMIN')
  }

  isEnablerOrAdmin() {
    return this.user?.roles.some(
      (role) => role.code === 'ENABLER' || role.code === 'SUPER_ADMIN',
    )
  }

  canSeeMemberInfo() {
    return (
      this.privacySetting === 'FULLY_OPEN' ||
      this.privacySetting === 'PARTIALLY_OPEN'
    )
  }

  canSeeRoleplay() {
    return isFeatureAllowed('ROLEPLAY', this.user?.enabledFeatures)
  }

  isAssessmentSettingsVisible(): boolean {
    return (
      !!this.isEnablerOrAdmin() &&
      this.user != null &&
      isAssessmentSettingsPageVisible(this.user)
    )
  }

  canSeeMeetingRecording() {
    return isFeatureAllowed('MEETING_RECORDING', this.user?.enabledFeatures)
  }

  canSeeKpiDashboards() {
    return this.hasKpiDashboards
  }

  isEvaluationSkipped() {
    return isFeatureAllowed('SKIP_AI_EVALUATION', this.user?.enabledFeatures)
  }

  private getCurrentPage(
    url: string,
  ):
    | 'HOME'
    | 'USER_LIST'
    | 'ROLEPLAY'
    | 'ASSESSMENTS'
    | 'KPI_DASHBOARD'
    | 'DASHBOARD'
    | 'SETTINGS'
    | 'ADMIN' {
    if (/^\/settings(?:[/?#;]|$)/.test(url)) {
      return 'SETTINGS'
    }
    if (url.includes('admin')) {
      return 'ADMIN'
    }
    if (url.includes('users')) {
      return 'USER_LIST'
    }
    if (url.includes('roleplay')) {
      return 'ROLEPLAY'
    }
    if (url.includes('assessments')) {
      return 'ASSESSMENTS'
    }
    if (url.includes('kpi-dashboards')) {
      return 'KPI_DASHBOARD'
    }
    if (url.includes('dashboard')) {
      return 'DASHBOARD'
    }

    return 'HOME'
  }
}
